/* Costack Gap-Closer Upsell
 * Cart-drawer hero that suggests ONE gap-sized, one-time product to reach the
 * free-shipping threshold in as few taps as possible. Reuses the costack-cro.js
 * add-on click handler (.costack-bar__addon) so tapping adds the item and
 * re-renders the drawer. Only manages its own mount node.
 *
 * 15 Sep 2026: THE UPSELL WAS DEAD. The candidate list was five hardcoded variant
 * ids from 13 Jul, and three of them had since sold out. Candidates are now read
 * from the live catalogue and filtered on availability, so a stockout takes a
 * product out of the upsell instead of breaking it. If an add still fails the
 * shopper is told and a different product is offered.
 *
 * 15 Sep 2026, SECOND PASS: the overshoot cap was measured against the GAP, not
 * the cart. A 3.99 cart has a 31.01 gap, so a 37.98 bundle sat inside the band and
 * got offered to save 1.99 of postage. Nobody spends ten times their basket to
 * dodge 1.99, and the ask reads as a joke. The unlock pitch now only runs when the
 * gap is proportionate to what the shopper has already committed. Otherwise the
 * hero drops to a modest add-on, never dearer than the cart itself, and the copy
 * switches to the honest "you are X away" framing. Caught on a phone recording.
 *
 * 15 Sep 2026, THIRD PASS: the mount is looked up across the whole document, not
 * just inside .drawer__inner. On /cart the buy-path script moves this mount out of
 * the off-canvas drawer and into the visible totals column; a drawer-scoped lookup
 * would not find it there, build a second one back inside the drawer, and the two
 * would fight each other on every mutation tick.
 */
(function () {
  'use strict';
  var CFG = window.CostackCRO || {};
  var THRESHOLD = (CFG.freeShippingThresholdPence != null) ? CFG.freeShippingThresholdPence : 3500;
  var OVERSHOOT = 800;

  var SOURCES = ['/collections/all-spices-and-seasonings/products.json?limit=250', '/collections/bundles/products.json?limit=250'];
  var SKIP = /(ebook|subscription|monthly)/i;

  var candidates = null;
  var loading = null;
  var blocked = {};

  function loadCandidates() {
    if (candidates) return Promise.resolve(candidates);
    if (loading) return loading;
    loading = Promise.all(SOURCES.map(function (u) {
      return fetch(u, { credentials: 'same-origin' }).then(function (r) { return r.json(); }).catch(function () { return { products: [] }; });
    })).then(function (sets) {
      var seen = {}, out = [];
      sets.forEach(function (s) {
        (s.products || []).forEach(function (p) {
          if (SKIP.test(p.handle || '')) return;
          var v = (p.variants || []).filter(function (x) { return x.available; })[0];
          if (!v || seen[v.id]) return;
          seen[v.id] = true;
          var img = (p.images && p.images[0] && p.images[0].src) || '';
          out.push({
            id: v.id,
            name: p.title,
            price: Math.round(parseFloat(v.price) * 100),
            img: img ? img.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : ''
          });
        });
      });
      candidates = out;
      return out;
    });
    return loading;
  }

  function fmt(p) { return '£' + (p / 100).toFixed(2); }
  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  var lastSig = '';
  function drawer() { return document.querySelector('cart-drawer'); }

  function ensureMount() {
    var d = drawer(); if (!d) return null;
    var inner = d.querySelector('.drawer__inner') || d;
    var mount = document.querySelector('.costack-gapcloser-mount');
    if (mount) return mount;
    mount = document.createElement('div');
    mount.className = 'costack-gapcloser-mount';
    lastSig = '';
    var footer = d.querySelector('.cart-drawer__footer');
    var existingBar = d.querySelector('.costack-bar-mount');
    if (existingBar && existingBar.parentNode) { existingBar.parentNode.insertBefore(mount, existingBar); }
    else if (footer) { footer.insertBefore(mount, footer.firstChild); }
    else { inner.appendChild(mount); }
    return mount;
  }

  function choose(cart, list) {
    var inCart = {};
    (cart.items || []).forEach(function (it) { inCart[it.variant_id] = true; });
    var avail = list.filter(function (c) { return !inCart[c.id] && !blocked[c.id] && c.price > 0; });
    if (!avail.length) return null;
    var gap = THRESHOLD - cart.total_price;
    var sub = cart.total_price;

    /* Only pitch "unlock free delivery" when the ask is proportionate to the cart.
       Floor of 10.00 so a nearly-qualifying small basket still gets the nudge. */
    if (gap <= Math.max(sub * 2, 1000)) {
      var crossers = avail.filter(function (c) { return c.price >= gap && c.price <= gap + OVERSHOOT; })
        .sort(function (a, b) { return a.price - b.price; });
      if (crossers.length) return { hero: crossers[0], unlocks: true, gap: gap };
    }

    /* Otherwise a modest add-on, never dearer than what is already in the cart. */
    var ceiling = Math.max(sub, 500);
    var under = avail.filter(function (c) { return c.price <= ceiling && c.price < gap; })
      .sort(function (a, b) { return b.price - a.price; });
    if (under.length) return { hero: under[0], unlocks: false, gap: gap };

    var cheap = avail.slice().sort(function (a, b) { return a.price - b.price; });
    if (cheap.length && cheap[0].price < gap) return { hero: cheap[0], unlocks: false, gap: gap };
    return null;
  }

  function render(cart) {
    var mount = ensureMount(); if (!mount) return;
    if (!cart || !cart.item_count || cart.total_price >= THRESHOLD) { if (mount.innerHTML) { mount.innerHTML = ''; lastSig = ''; } return; }
    loadCandidates().then(function (list) {
      var pick = choose(cart, list); if (!pick) { if (mount.innerHTML) { mount.innerHTML = ''; lastSig = ''; } return; }
      var sig = pick.hero.id + ':' + pick.unlocks + ':' + cart.total_price;
      if (sig === lastSig && mount.innerHTML) return;
      lastSig = sig;
      var h = pick.hero;
      var headline = pick.unlocks
        ? 'Add this and unlock <strong>FREE UK delivery</strong>'
        : "You're " + fmt(pick.gap) + " from <strong>FREE UK delivery</strong>";
      mount.innerHTML =
        '<div class="cstk-gap" style="margin:0 0 12px;padding:12px;border:1px solid #f1d36a;background:#fff8ea;border-radius:10px;">'
        + '<div style="font-size:13px;color:#5a3d00;margin-bottom:8px;">' + headline + '</div>'
        + '<div style="display:flex;align-items:center;gap:10px;">'
        + (h.img ? '<img src="' + esc(h.img) + '" alt="" width="48" height="48" style="width:48px;height:48px;border-radius:8px;object-fit:cover;flex:0 0 auto;background:#fff;">' : '')
        + '<div style="flex:1 1 auto;min-width:0;">'
        + '<div style="font-size:13px;font-weight:700;color:#231900;line-height:1.2;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">' + esc(h.name) + '</div>'
        + '<div style="font-size:13px;color:#5a4f37;">' + fmt(h.price) + '</div>'
        + '</div>'
        + '<button type="button" class="costack-bar__addon cstk-gap__btn" data-variant-id="' + h.id + '" data-original-name="' + esc(h.name) + '" style="flex:0 0 auto;background:#b3641c;color:#fff;border:0;border-radius:999px;padding:10px 16px;font-size:13px;font-weight:700;cursor:pointer;min-height:44px;white-space:nowrap;">Add</button>'
        + '</div>'
        + '<p class="cstk-gap__msg" role="status" style="margin:8px 0 0;font-size:12px;font-weight:600;color:#9a3412;display:none;"></p>'
        + '</div>';
    });
  }

  /* If the shared handler cannot add the hero, say so and offer another product
     rather than leaving a button that looks live and does nothing. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.cstk-gap__btn');
    if (!btn) return;
    var id = parseInt(btn.getAttribute('data-variant-id'), 10);
    var before = null;
    fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (c) { before = c.item_count; });
    setTimeout(function () {
      fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (after) {
        var landed = (after.items || []).some(function (it) { return it.variant_id === id; });
        if (landed || before === null) return;
        blocked[id] = true;
        if (candidates) candidates = candidates.filter(function (c) { return c.id !== id; });
        var mount = document.querySelector('.costack-gapcloser-mount');
        var msg = mount && mount.querySelector('.cstk-gap__msg');
        if (msg) { msg.textContent = 'That one just sold out. Here is another that closes the gap.'; msg.style.display = 'block'; }
        lastSig = '';
        setTimeout(function () { refresh(); }, 1400);
      }).catch(function () {});
    }, 2200);
  }, true);

  var fetchFn = window.fetch;
  function refresh() {
    try {
      fetchFn('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) { render(cart); })
        .catch(function () {});
    } catch (e) {}
  }

  function observe() {
    var d = drawer(); if (!d || d.__cstkGapObserved) return;
    d.__cstkGapObserved = true;
    var t = null;
    var obs = new MutationObserver(function (muts) {
      var relevant = false;
      for (var i = 0; i < muts.length; i++) {
        var tgt = muts[i].target;
        if (tgt && tgt.closest && tgt.closest('.costack-gapcloser-mount')) continue;
        relevant = true; break;
      }
      if (!relevant) return;
      if (t) clearTimeout(t);
      t = setTimeout(refresh, 150);
    });
    obs.observe(d, { childList: true, subtree: true });
  }

  function init() {
    loadCandidates();
    observe();
    refresh();
    document.documentElement.addEventListener('cart:refresh', function () { setTimeout(refresh, 80); });
    document.addEventListener('cart:refresh', function () { setTimeout(refresh, 80); });
    try {
      var po = new PerformanceObserver(function (list) {
        var e = list.getEntries();
        for (var i = 0; i < e.length; i++) { if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i.test(e[i].name || '')) { setTimeout(refresh, 180); return; } }
      });
      po.observe({ entryTypes: ['resource'] });
    } catch (e) {}
    setTimeout(observe, 1500);
    setTimeout(observe, 4000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
